import {
  buildActionableSummary,
  resolveRemediation,
} from "./remediationCatalog.js";
import { normalizeRemediationSteps } from "./remediationDisplay.js";

export const SUMMARY_GROUP_LABELS = Object.freeze({
  CRITICAL: "Fix now",
  HIGH: "This sprint",
  MEDIUM: "Plan",
  LOW: "Hygiene",
});

export const EMPTY_GROUP_MESSAGES = Object.freeze({
  CRITICAL: "No fix-now items — nothing critical open.",
  HIGH: "No this-sprint items — no high-severity gaps open.",
  MEDIUM: "No planned improvements — medium-severity backlog is clear.",
  LOW: "No hygiene items — low-severity cleanup is clear.",
});

export const RUNWAY_COLORS = Object.freeze({
  CRITICAL: "#FF5964",
  HIGH: "#FF9F45",
  MEDIUM: "#F5C842",
  LOW: "#6B7688",
});

function formatFindingFrameworks(finding) {
  const refs = finding?.frameworks || finding?.framework_refs || [];
  if (!Array.isArray(refs) || refs.length === 0) return null;
  const parts = refs
    .map((r) => {
      if (typeof r === "string") return r.trim();
      if (!r || typeof r !== "object") return "";
      const fw = String(r.framework || "").trim();
      const ctrl = String(r.control || "").trim();
      if (fw && ctrl) return `${fw} §${ctrl}`;
      return fw || ctrl;
    })
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function buildFindingDetailLookup(snapshot) {
  const map = new Map();
  const add = (f) => {
    if (!f || typeof f !== "object") return;
    const id = f.rule_id || f.ruleId;
    if (id) map.set(String(id), f);
  };

  const assessment = snapshot?.assessment?.findings;
  if (Array.isArray(assessment)) {
    for (const f of assessment) add(f);
  }

  const legacy = snapshot?.findings || {};
  for (const bucket of ["critical", "warnings", "other"]) {
    const list = Array.isArray(legacy[bucket]) ? legacy[bucket] : [];
    for (const f of list) add(f);
  }

  return map;
}

function buildExecLeadLine(fixNow, thisSprint) {
  if (fixNow === 0 && thisSprint === 0) {
    return "No critical or high-severity gaps — focus on planned improvements and hygiene.";
  }
  if (fixNow === 0) {
    return `Start with ${thisSprint} high-severity item${thisSprint === 1 ? "" : "s"} this sprint — no critical gaps open.`;
  }
  if (thisSprint === 0) {
    return `Start with ${fixNow} critical gap${fixNow === 1 ? "" : "s"} before lower-priority work.`;
  }
  return `Start with ${fixNow} critical gap${fixNow === 1 ? "" : "s"}, then ${thisSprint} high-severity item${thisSprint === 1 ? "" : "s"} this sprint.`;
}

export function buildRemediationSummary(snapshot, providerId) {
  const actionable = buildActionableSummary(snapshot, providerId);
  const lookup = buildFindingDetailLookup(snapshot);

  const groups = (actionable.groups || []).map((group) => {
    const severity = group.severity;
    const expandedDefault = severity === "CRITICAL" || severity === "HIGH";
    const findings = (group.findings || []).map((item) => {
      const ruleId = item.rule_id || item.ruleId;
      const source = lookup.get(String(ruleId || "")) || {};
      const detail = source.detail || source.description || "";
      const frameworks = source.frameworks || source.framework_citations || [];
      const rawRemediation = item.remediation || resolveRemediation(ruleId, providerId);
      const remediation = rawRemediation
        ? {
            ...rawRemediation,
            remediation_steps: normalizeRemediationSteps(rawRemediation.remediation_steps),
          }
        : null;
      return {
        ...item,
        detail,
        framework_line: formatFindingFrameworks({ frameworks }),
        remediation,
      };
    });

    return {
      ...group,
      display_label: SUMMARY_GROUP_LABELS[severity] || group.urgency_label,
      empty_message: EMPTY_GROUP_MESSAGES[severity] || "No open items in this horizon.",
      runway_color: RUNWAY_COLORS[severity] || "#6B7688",
      expanded_default: expandedDefault,
      collapsible: !expandedDefault,
      findings,
    };
  });

  const countFor = (sev) => groups.find((g) => g.severity === sev)?.count || 0;
  const fixNow = countFor("CRITICAL");
  const thisSprint = countFor("HIGH");
  const plan = countFor("MEDIUM");
  const hygiene = countFor("LOW");

  return {
    provider_id: actionable.provider_id,
    open_total: actionable.open_total,
    severity_source: actionable.severity_source,
    caveat: actionable.caveat,
    exec_brief: {
      lead_line: buildExecLeadLine(fixNow, thisSprint),
      fix_now_count: fixNow,
      this_sprint_count: thisSprint,
      plan_count: plan,
      hygiene_count: hygiene,
      runway: [
        { severity: "CRITICAL", label: SUMMARY_GROUP_LABELS.CRITICAL, count: fixNow, color: RUNWAY_COLORS.CRITICAL },
        { severity: "HIGH", label: SUMMARY_GROUP_LABELS.HIGH, count: thisSprint, color: RUNWAY_COLORS.HIGH },
        { severity: "MEDIUM", label: SUMMARY_GROUP_LABELS.MEDIUM, count: plan, color: RUNWAY_COLORS.MEDIUM },
        { severity: "LOW", label: SUMMARY_GROUP_LABELS.LOW, count: hygiene, color: RUNWAY_COLORS.LOW },
      ],
    },
    groups,
  };
}
